import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, retry } from 'rxjs';
import { ISelect } from '../../components/gs-object-select/interfaces/object-select';
import { IPaginated } from '../../models';
import { IPageSelectItem, PageStatusType, PageType } from '../../models/pages/page';
import { encodeBase64Unicode } from '../../services/encoding/encoding.service';
import { DataProvider } from '../basic/data.service';

/** Parameters accepted by the page select endpoint */
export interface PageSelectParams {
  /** Page index to load, starting at 0 */
  pageIndex?: number;
  /** Amount of pages per result page */
  pageSize?: number;
  /** Search term, sent base64 encoded */
  search?: string;
  /** Only list the direct sub pages of this page; omitted lists the top level */
  parentID?: number;
  /** Restrict the result to these page types */
  pageTypes?: PageType[];
  /** Restrict the result to pages with this publication state */
  pageStatusType?: PageStatusType;
}

@Injectable({
  providedIn: 'root'
})
export class PageSelectProvider extends DataProvider<never> {
  // Properties & Signals
  /** The endpoint listing selectable pages. */
  override endpoint = 'f/select/pages';

  // Methods
  /**
   * Retrieves a paginated list of selectable pages.
   * @param params - Search, pagination and filter parameters
   * @returns Observable of the paginated list of selectable pages
   */
  selectPages(params: PageSelectParams = {}): Observable<IPaginated<ISelect<IPageSelectItem>>> {
    let httpParams = new HttpParams()
      .set('pageIndex', (params.pageIndex ?? 0).toString())
      .set('pageSize', (params.pageSize ?? 10).toString());

    const search = params.search?.trim();
    if (search) httpParams = httpParams.set('search', encodeBase64Unicode(search));
    if (params.parentID) httpParams = httpParams.set('parentID', params.parentID.toString());
    if (params.pageTypes?.length) httpParams = httpParams.set('pageTypes', params.pageTypes.join(','));
    if (params.pageStatusType != null) httpParams = httpParams.set('pageStatusType', params.pageStatusType.toString());

    return this.http
      .get<IPaginated<ISelect<IPageSelectItem>>>(this.endpointWithUrl, {
        ...this.httpOptions,
        params: httpParams
      })
      .pipe(
        map((data) => {
          return DataProvider.mapCasesReverse(data);
        }),
        retry(1),
        catchError(this.errorService.convert)
      );
  }
}
